import {useForm} from 'antd/lib/form/Form'
import {useCallback, useMemo, useState} from 'react'
import validator from '../classes/validator'
import extractor from '../classes/extractor'
import GlobalOptionsInterface, {CalcMode} from '../types/global-options'
import Api from '../classes/api'
import {
    createErrorState,
    createInitialState,
    createStartedState,
    createSuccessState,
} from '../../../../common/helpers/create-request-state'
import CostInfoInterface from '../types/models/cost-info'
import CostStateType from '../types/state/cost-state'
import GetPaymentLinkRequestInterface from '../types/requests/get-payment-link'
import PolicyInfoInterface from '../types/models/policy-info'
import PolicyStateType from '../types/state/policy-state'
import PaymentLinkStateType from '../types/state/payment-link-state'
import PaymentLinkInfoInterface from '../types/models/payment-link-info'
import useSteps from '../../../common/hooks/use-steps'
import bonusesSystemUsed from '../helpers/bonuses-system-used'
import {log} from '../../../../common/helpers/console/log'
import SendPaymentLinkRequestInterface from '../types/requests/send-payment-link'
import SendPaymentLinkStateType from '../types/state/send-payment-link-state'
import showModalWithDemoRestriction from '../../../../common/helpers/modals/show-modal-with-demo-restriction'

interface UseControllerArgsInterface {
    globalOptions: GlobalOptionsInterface
}

const costInitState = createInitialState<CostInfoInterface>(),
    policyInitState = createInitialState<PolicyInfoInterface>(),
    paymentLinkInitState = createInitialState<PaymentLinkInfoInterface>(),
    sendPaymentLinkState = createInitialState<null>()

const useController = ({globalOptions}: UseControllerArgsInterface) => {
    const [form] = useForm(),
        {currentStep, prevStep, nextStep} = useSteps(),
        [cost, setCost] = useState<CostStateType>(costInitState),
        [policy, setPolicy] = useState<PolicyStateType>(policyInitState),
        [paymentLink, setPaymentLink] = useState<PaymentLinkStateType>(paymentLinkInitState),
        [sendPaymentLink, setSendPaymentLink] =
            useState<SendPaymentLinkStateType>(sendPaymentLinkState),
        bonusesSystemIsOn = useMemo(() => bonusesSystemUsed(globalOptions), []),
        /*
            Пересчет стоимости,
            поля должны быть валидны!
         */
        recalculate = async () => {
            // Сразу устанавливаем флаг, о начале запроса стоимости
            setCost(createStartedState())
            try {
                // Извлекаем данные для получения стоимости
                const data = await extractor.extractDataForGetCost()
                const result = await Api.getCost(data)
                // Проверяем результат запроса
                if ('errors' in result) throw new Error(result.errors.join())
                setCost(createSuccessState(result))
                return true
            } catch (e) {
                setCost(createErrorState([e.message]))
                console.error(e)
                return false
            }
        },
        createPolicy = useCallback(async (): Promise<null | PolicyInfoInterface> => {
            log('createPolicy')
            setPolicy(createStartedState())
            const dataForCreate = await extractor.extractDataForCreate()
            log('Извлечены данные для создания полиса ', dataForCreate)
            const creationResult = await Api.createPolicy(dataForCreate)
            if ('errors' in creationResult) {
                setPolicy(createErrorState(creationResult.errors))
                return null
            }
            setPolicy(createSuccessState(creationResult))
            return creationResult
        }, []),
        getPaymentLink = useCallback(
            async (policyId: number): Promise<null | PaymentLinkInfoInterface> => {
                log('getPaymentLink')
                setPaymentLink(createStartedState())
                const dataForGetPaymentLink: GetPaymentLinkRequestInterface = {
                        id: policyId,
                        code: extractor.extractCode(),
                    },
                    gettingResult = await Api.getPaymentLink(dataForGetPaymentLink)
                if ('errors' in gettingResult) {
                    setPaymentLink(createErrorState(gettingResult.errors))
                    return null
                }
                setPaymentLink(createSuccessState(gettingResult))
                return gettingResult
            },
            []
        ),
        redirectToPayment = useCallback((url: string) => {
            if (ENV_IS_DEV) {
                if (confirm('Перейти на оплату?')) window.location.href = url
            } else {
                window.location.href = url
            }
        }, []),
        /*
            Обработчик перехода с первого на второй шаг
            Параметры поездки -> Наполнение
         */
        fromFirstToSecondStep = useCallback(async e => {
            console.log('fromFirstToSecondStep')
            e.preventDefault()
            const validationStatus = await validator.validateFirstStep()
            if (validationStatus === 'error') return
            // Сразу устанавливаем флаг, о начале запроса стоимости
            setCost(createStartedState())
            const isSuccess = await recalculate()
            if (isSuccess) nextStep()
        }, []),
        /*
            Обработчик перехода со второго на третий шаг
            Наполнение -> Данные и оплата
         */
        fromSecondToThirdStep = useCallback(async e => {
            console.log('fromSecondToThirdStep')
            e.preventDefault()
            const validationStatus = await validator.validateSecondStep()
            if (validationStatus === 'error') return
            const isSuccess = await recalculate()
            if (isSuccess) nextStep()
        }, []),
        /*
            Обработчик перехода с третьего на четвертый шаг
            Данные и оплата -> Проверка телефона
         */
        // fromThirdToFourthStep = useCallback(async e => {
        //     console.log('fromThirdToFourthStep')
        //     e.preventDefault()
        //     const validationStatus = await validator.validateThirdStep()
        //     if (validationStatus === 'error') return
        //     nextStep()
        // }, []),
        /*
            Обработчик перехода с третьего на четвертый шаг для агента
            Данные и оплата -> Оплата
         */
        fromThirdToFourthStepForAgentMode = useCallback(async e => {
            console.log('fromThirdToFourthStepForAgentMode')
            e.preventDefault()

            // Если калькулятор в демо режиме
            if (globalOptions.isDemo) return showModalWithDemoRestriction()

            // Валидируем третий шаг и НЕ переходим ко вводу телефона,
            // а сразу создаем полис и идем на оплату
            const validationStatus = await validator.validateThirdStep()
            if (validationStatus === 'error') return
            // Создаем полис
            const creationResult = await createPolicy()
            if (creationResult === null) return
            // Получаем ссылку на оплату
            const gettingResult = await getPaymentLink(creationResult.id)
            redirectToPayment(gettingResult.data.url)
        }, []),
        /*
            Обработчик перехода с четвертого на пятый шаг
            Проверка телефона -> Оплата
         */
        fromThirdToFourthStep = useCallback(async e => {
            log('fromThirdToFourthStep')
            // Делаем валидацию полей
            const validationStatus = await validator.validateThirdStep()
            if (validationStatus === 'error') return
            // Создаем полис
            const creationResult = await createPolicy()
            if (creationResult === null) return
            // Получаем ссылку и редиректим
            const gettingResult = await getPaymentLink(creationResult.id)
            redirectToPayment(gettingResult.data.url)
        }, []),
        /*
            Обработчик нажатия кнопки отправки ссылки на почту
         */
        handleSendPaymentLinkToEmail = useCallback(async e => {
            log('handleSendPaymentLinkToEmail')
            e.preventDefault()

            // Если калькулятор в демо режиме
            if (globalOptions.isDemo) return showModalWithDemoRestriction()

            // Делаем валидацию полей
            const validationStatus = await validator.validateThirdStep()
            if (validationStatus === 'error') return
            // Создаем полис
            const creationResult = await createPolicy()
            if (creationResult === null) return
            // Если успешно, пытаемся отправить ссылку на email
            const data: SendPaymentLinkRequestInterface = {
                policyId: creationResult.id,
            }
            setSendPaymentLink(createStartedState())
            const result = await Api.sendPaymentLinkToEmail(data)
            log('Результат отправки ссылки на оплату.')
            if ('errors' in result) return setSendPaymentLink(createErrorState(result.errors))
            setSendPaymentLink(createSuccessState(null))
        }, []),
        handleNextClick = useCallback(
            e => {
                if (globalOptions.calcMode === CalcMode.Agent) {
                    if (currentStep === 2) return fromThirdToFourthStepForAgentMode(e)
                }
                if (currentStep === 0) return fromFirstToSecondStep(e)
                if (currentStep === 1) return fromSecondToThirdStep(e)
                if (currentStep === 2) return fromThirdToFourthStep(e)
                // if (currentStep === 3) return fromFourthToFifthStep(e)
            },
            [currentStep]
        )

    validator.setForm(form)
    extractor.setForm(form)
    extractor.setGlobalOptions(globalOptions)
    extractor.setBonusesSystemIsOn(bonusesSystemIsOn)

    return {
        form,
        currentStep,
        cost,
        policy,
        paymentLink,
        prevStep,
        recalculate,
        bonusesSystemIsOn,
        handleNextClick,
        sendPaymentLink,
        handleSendPaymentLinkToEmail,
    }
}

export default useController
